// The token requests the portal makes that may exchange the shared Auth0 refresh token run under this
// one Web Lock, shared by every tab of the portal origin (AB#5012).
//
// With multi-resource refresh tokens (MRRT) one rotating refresh token serves every API, and tabs
// share it through the localStorage cache. auth0-spa-js locks only per audience, so two APIs, or two
// tabs, could exchange the same refresh token at once. Auth0 then sees a rotated token reused and,
// beyond the client's short reuse leeway, which covers only the immediately previous token, revokes
// the whole token family, signing the person out everywhere. Run one at a time, each exchange reads the
// refresh token the previous one stored; the leeway is left for a response lost to a page reload. That
// holds because the token handed to the SDK comes from IndexedDB (auth/auth0Cache, AB#5741): localStorage
// reaches a tab asynchronously, not in order with this lock, and could still hand it a rotated token.
//
// Not every exchange takes this lock: auth0-react's session check on page load (checkSession) exchanges
// outside it when the cached Management token has expired. The client's 10-second rotation overlap
// covers that one exchange meeting another tab's.
//
// A token still fresh in the shared cache needs no exchange and is read without this lock
// (auth/cachedAccessToken), so once an API's token is cached its requests no longer queue here.
// Requests that joined the queue before that API's first exchange still wait their turn. Any request
// that might exchange, or rewrite the cache, takes the lock.
export const TOKEN_REQUEST_LOCK_NAME = 'zespy-portal.auth0.token-request';

export class TokenRequestLockTimeoutError extends Error {
  constructor() {
    super('Another Auth0 token request held the token lock for too long.');
    this.name = 'TokenRequestLockTimeoutError';
  }
}

const readLockManager = (): LockManager | undefined => {
  try {
    return typeof navigator !== 'undefined' && typeof navigator.locks?.request === 'function'
      ? navigator.locks
      : undefined;
  } catch {
    return undefined;
  }
};

// Without Web Locks (no browser the portal supports), requests are still serialised in this tab.
let inTabQueue: Promise<unknown> = Promise.resolve();

const runInTabQueue = <T,>(request: () => Promise<T>): Promise<T> => {
  const run = inTabQueue.then(request, request);
  inTabQueue = run.catch(() => undefined);
  return run;
};

/**
 * Runs one token request while holding the portal's token lock. A request still waiting for the lock
 * after `waitTimeoutMs` gives up with {@link TokenRequestLockTimeoutError} instead of running late.
 */
export const withTokenRequestLock = async <T,>(request: () => Promise<T>, waitTimeoutMs: number): Promise<T> => {
  const locks = readLockManager();
  if (!locks) return runInTabQueue(request);

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), waitTimeoutMs);
  try {
    return await locks.request(TOKEN_REQUEST_LOCK_NAME, { mode: 'exclusive', signal: controller.signal }, () => {
      clearTimeout(timeout);
      return request();
    });
  } catch (error) {
    if (controller.signal.aborted && error instanceof DOMException && error.name === 'AbortError') {
      throw new TokenRequestLockTimeoutError();
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
};
